<template>
    <div class="media-button-container">
        <q-btn
            v-if="showButton"
            color="accent"
            @click="openGallery(media)"
            icon="fa-regular fa-images"
            flat
            dense
        >
            <q-chip
                :label="mediaCount"
                text-color="white"
                color="accent"
                size="md"
                dense
                class="media-button-chip"
            />
            <q-tooltip v-if="!exceedsMaxMediaThreshold">
                {{ $t('Display media') }}
            </q-tooltip>
            <q-tooltip v-else>
                {{
                    $t(
                        'The maximum number of media ({max}) has been exceeded.',
                        {
                            max: MAX_MEDIA_COUNT
                        }
                    )
                }}
            </q-tooltip>
        </q-btn>
    </div>
    <q-dialog v-model="showDialog">
        <q-spinner v-if="isLoading" color="primary" size="md" />
        <q-card v-else>
            <q-bar class="bg-primary text-white">
                <q-icon name="fa-regular fa-images" />
                <div class="text-white">
                    {{ $t('Display media') }}
                </div>
                <q-space />
                <q-btn icon="close" dense flat v-close-popup>
                    <q-tooltip>{{ $t('Close') }}</q-tooltip>
                </q-btn>
            </q-bar>
            <q-card-section class="media-main">
                <q-btn
                    flat
                    icon="fa-solid fa-chevron-left"
                    @click="prevImage"
                    :disable="currentIndex === 0"
                    class="btn-prev"
                    :ripple="false"
                />
                <q-img
                    v-if="currentImage"
                    :src="currentImage.url"
                    spinner-color="secondary"
                    class="media-img"
                    fit="scale-down"
                />
                <q-btn
                    flat
                    icon="fa-solid fa-chevron-right"
                    @click="nextImage"
                    :disable="currentIndex === media.length - 1"
                    class="btn-next"
                    :ripple="false"
                />
            </q-card-section>
            <q-card-section class="row justify-center q-gutter-sm">
                <q-img
                    v-for="(img, index) in media"
                    :key="index"
                    :src="img.url"
                    class="media-thumbnail"
                    :class="{
                        'media-thumbnail-selected': currentIndex === index
                    }"
                    @click="selectImage(index)"
                    contain
                />
            </q-card-section>
        </q-card>
    </q-dialog>
</template>

<script>
import { loadPhaseMedia } from '@utils/mediaManager.js';
import { MAX_MEDIA_COUNT } from '@utils/mediaManager.js';

export default {
    name: 'MediaGalleryButton',
    props: {
        mediaIds: {
            type: Array,
            required: true,
            default: () => []
        },
        phaseId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            showDialog: false,
            currentIndex: 0,
            media: [],
            isLoading: false,
            MAX_MEDIA_COUNT
        };
    },
    computed: {
        /**
         * Returns the number of media.
         * @returns {number} - The number of media.
         */
        mediaCount() {
            return this.mediaIds.length;
        },
        /**
         * Returns whether the show button should be displayed.
         * @returns {boolean} - True if there is at least one media, false otherwise.
         */
        showButton() {
            return this.mediaCount > 0;
        },
        /**
         * Returns the current image.
         * @returns {string} - The current image.
         */
        currentImage() {
            return this.media[this.currentIndex];
        },
        /**
         * Returns whether the maximum number of media has been exceeded.
         * @returns {boolean} - True if the maximum number of media has been exceeded, false otherwise.
         */
        exceedsMaxMediaThreshold() {
            return this.mediaCount > this.MAX_MEDIA_COUNT;
        }
    },
    methods: {
        loadPhaseMedia,
        async openGallery() {
            this.currentIndex = 0;
            try {
                // Load media from the API
                const medias = await this.loadMedias();

                this.showDialog = true;

                this.media = medias || [];
            } catch (error) {
                // Let the component handle the error
                this.$notifyError(this.$t('Failed to load media.'), error);
            }
        },
        selectImage(index) {
            this.currentIndex = index;
        },
        nextImage() {
            if (this.currentIndex < this.media.length - 1) {
                this.currentIndex++;
            }
        },
        prevImage() {
            if (this.currentIndex > 0) {
                this.currentIndex--;
            }
        },
        /**
         * Load media from the API.
         * @param {Array} ids - Array of media ids.
         * @returns {Promise} Promise object representing the API request.
         */
        async loadMedias() {
            try {
                this.isLoading = true;
                const medias = await this.loadPhaseMedia(this.phaseId);
                return medias;
            } finally {
                this.isLoading = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.media-button-container {
    position: relative;
}

.media-button-chip {
    position: absolute;
    top: -10px;
    right: -10px;
    z-index: 1;
}

.media-main {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 70vw;
    height: 60vh;
}

.btn-prev,
.btn-next {
    position: absolute;
    top: 0;
    z-index: 1;
    height: calc(100% - 32px);
    margin: 16px;
    background-color: transparent;
}

.btn-prev {
    left: 0;
}

.btn-next {
    right: 0;
}

.media-img {
    width: 100%;
    height: 100%;
}

.media-thumbnail {
    width: 70px;
    height: 70px;
    cursor: pointer;
    border: 2px solid transparent;
}

.media-thumbnail-selected {
    border-color: $accent;
}

.q-dialog__inner--minimized > div {
    max-width: 100% !important;
}
</style>
